import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { QueryState } from "@/shared/ui/QueryState";
import { Button, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import {
  useDeletePackageTemplate,
  usePackageTemplateQuery,
  useUpdatePackageTemplate,
} from "../api/queries";
import { PackageTemplateForm } from "../ui/PackageTemplateForm";

export function PackageTemplateEditPage() {
  const nav = useNavigate({
    from: "/app/packages/templates/$id/edit",
  });
  const { id } = useParams({ from: "/app/packages/templates/$id/edit" });
  const { data, ...result } = usePackageTemplateQuery(id);
  const updateTemplate = useUpdatePackageTemplate(id);
  const deleteTemplate = useDeletePackageTemplate();

  const template = data;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "Template ແພັກເກດ", to: "/app/packages/templates" },
            { label: "ແກ້ໄຂ" },
          ]}
        />
      </Header>

      <Main>
        <div className="flex flex-wrap items-center justify-between space-y-2">
          <div>
            <h2 className="flex items-center font-bold text-2xl tracking-tight">
              ແກ້ໄຂ Package Template
            </h2>
            <p className="text-muted-foreground">
              ປັບປຸງລາຍລະອຽດຂອງ template ແພັກເກດ.
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="destructive"
              onClick={async () => {
                const ok = await import("@devhop/ui").then((m) =>
                  m.confirm({
                    title: "ລຶບ template ແພັກເກດ",
                    description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບ template ແພັກເກດນີ້?",
                    actionText: "ລຶບ",
                    ActionProps: {
                      variant: "destructive",
                    },
                  }),
                );
                if (!ok) return;

                try {
                  await toast.promise(deleteTemplate.run(id), {
                    loading: "ກຳລັງລຶບ...",
                    success: () => {
                      nav({ to: "/app/packages/templates" });
                      return "ລຶບ template ແພັກເກດສຳເລັດ";
                    },
                    error: "ລຶບ template ແພັກເກດລົ້ມເຫຼວ",
                  });
                } catch (error) {
                  // Error handling is done by toast.promise
                }
              }}
            >
              ລຶບ
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                nav({
                  to: "/app/packages/templates/$id",
                  params: { id },
                })
              }
            >
              <ArrowLeftIcon className="size-4" />
              ກັບຄືນ
            </Button>
          </div>
        </div>

        <QueryState
          result={result}
          title="ກຳລັງໂຫຼດ template ແພັກເກດ"
          description="ກຳລັງດຶງລາຍລະອຽດ"
          variant="fullscreen"
        >
          {!template ? null : (
            <div className="mt-6 rounded-xl border bg-card p-6">
              <PackageTemplateForm
                initialValues={{
                  name: template.name,
                  totalSessions: template.totalSessions,
                  validDays: template.validDays,
                  price: template.price,
                  description: template.description || "",
                  status: template.status,
                  maxSelectableClasses: template.maxSelectableClasses,
                  rules:
                    template.rules?.map((r) => ({
                      classTypeId: r.classTypeId,
                      levelId: r.levelId,
                    })) || [],
                }}
                onSubmit={async (vals) => {
                  try {
                    await updateTemplate.mutateAsync(vals);

                    nav({
                      to: "/app/packages/templates/$id",
                      params: { id },
                    });
                  } catch (error) {
                    // Error handling is done by the mutation
                  }
                }}
                submitting={updateTemplate.isPending}
              />
            </div>
          )}
        </QueryState>
      </Main>
    </>
  );
}
